import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader, SectionDivider } from "@/components/primitives";
import { listBeds, createBed, deleteBed } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, LayoutGrid, ChevronRight, Sprout } from "lucide-react";

export default function Beds() {
  const [beds, setBeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    name: "", width_cm: "", length_cm: "", row_spacing_cm: "30", notes: "",
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setBeds(await listBeds()); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        width_cm: form.width_cm === "" ? null : Number(form.width_cm),
        length_cm: form.length_cm === "" ? null : Number(form.length_cm),
        row_spacing_cm: form.row_spacing_cm === "" ? null : Number(form.row_spacing_cm),
        notes: form.notes || null,
      };
      const b = await createBed(payload);
      setBeds((prev) => [b, ...prev]);
      setForm({ name: "", width_cm: "", length_cm: "", row_spacing_cm: "30", notes: "" });
      toast.success("New bed marked on the plan");
    } catch {
      toast.error("Could not add bed");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try {
      await deleteBed(id);
      setBeds((prev) => prev.filter((b) => b.id !== id));
    } catch {
      toast.error("Could not remove");
    }
  };

  return (
    <div data-testid="beds-page">
      <PageHeader kicker="The Plot" title="Garden Beds" subtitle="Map your patch, then place your plants" />

      <form onSubmit={add} className="h-card p-5 mt-2" data-testid="bed-form">
        <div className="kicker mb-3">New Bed</div>
        <input
          className="field mb-3"
          placeholder="Bed name, e.g., West border"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          data-testid="bed-name"
          required
        />
        <div className="grid grid-cols-3 gap-2">
          <label className="block">
            <span className="text-xs text-ink-soft mb-1 inline-block">Width (cm)</span>
            <input type="number" min="10" max="10000" className="field" placeholder="200"
              value={form.width_cm} onChange={(e) => setForm({ ...form, width_cm: e.target.value })}
              data-testid="bed-width" />
          </label>
          <label className="block">
            <span className="text-xs text-ink-soft mb-1 inline-block">Length (cm)</span>
            <input type="number" min="10" max="10000" className="field" placeholder="300"
              value={form.length_cm} onChange={(e) => setForm({ ...form, length_cm: e.target.value })}
              data-testid="bed-length" />
          </label>
          <label className="block">
            <span className="text-xs text-ink-soft mb-1 inline-block">Spacing (cm)</span>
            <input type="number" min="5" max="500" className="field" placeholder="30"
              value={form.row_spacing_cm} onChange={(e) => setForm({ ...form, row_spacing_cm: e.target.value })}
              data-testid="bed-spacing" />
          </label>
        </div>
        <input
          className="field mt-3"
          placeholder="Notes, e.g., full sun, clay"
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          data-testid="bed-notes"
        />
        <div className="mt-4 flex justify-end">
          <button type="submit" className="btn-heritage" disabled={saving} data-testid="bed-submit">
            <Plus size={16} /> {saving ? "Adding..." : "Add Bed"}
          </button>
        </div>
      </form>

      <div className="mt-8">
        <SectionDivider label="Your Plots" />
      </div>

      {loading ? (
        <p className="text-center mt-8 font-display italic text-ink-soft">Sketching the map...</p>
      ) : beds.length === 0 ? (
        <p className="text-center mt-8 font-display italic text-ink-soft" data-testid="beds-empty">
          No beds yet, the plan is a blank page.
        </p>
      ) : (
        <div className="mt-6 grid sm:grid-cols-2 gap-4" data-testid="beds-grid">
          {beds.map((b) => <BedCard key={b.id} bed={b} onRemove={() => remove(b.id)} />)}
        </div>
      )}
    </div>
  );
}

function BedCard({ bed, onRemove }) {
  const hasSize = bed.width_cm && bed.length_cm;
  return (
    <article className="h-card p-4 flex flex-col gap-3 hover:shadow-seal transition-shadow" data-testid={`bed-${bed.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-lg bg-cream-deep flex items-center justify-center shrink-0">
          <LayoutGrid size={20} className="text-sage-dark" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-2xl text-sage-dark truncate">{bed.name}</div>
          <div className="text-sm text-ink-soft">
            {hasSize ? `${bed.width_cm}cm × ${bed.length_cm}cm` : (bed.dimensions || "no size set")}
            {bed.row_spacing_cm ? ` · ${bed.row_spacing_cm}cm spacing` : ""}
          </div>
          {bed.notes && <div className="mt-1 text-sm font-display italic text-ink-soft truncate">{bed.notes}</div>}
        </div>
        <button onClick={onRemove} className="text-terracotta hover:text-terracotta-dark" aria-label="Remove bed" data-testid={`bed-delete-${bed.id}`}>
          <Trash2 size={16} />
        </button>
      </div>

      {/* Miniature bed preview */}
      {hasSize && <BedMini width={bed.width_cm} length={bed.length_cm} spacing={bed.row_spacing_cm || 30} />}

      <Link to={`/beds/${bed.id}`} className="btn-heritage text-sm py-2 px-4 self-end" data-testid={`bed-open-${bed.id}`}>
        <Sprout size={14} /> Open planner <ChevronRight size={14} />
      </Link>
    </article>
  );
}

function BedMini({ width, length, spacing }) {
  const cols = Math.max(1, Math.round(width / spacing));
  const rows = Math.max(1, Math.round(length / spacing));
  const lines = [];
  for (let i = 1; i < cols; i++) lines.push(<line key={`v${i}`} x1={(i * width) / cols} y1={0} x2={(i * width) / cols} y2={length} stroke="#DDD3B8" strokeDasharray="1 2" strokeWidth="0.4" />);
  for (let i = 1; i < rows; i++) lines.push(<line key={`h${i}`} x1={0} y1={(i * length) / rows} x2={width} y2={(i * length) / rows} stroke="#DDD3B8" strokeDasharray="1 2" strokeWidth="0.4" />);
  return (
    <svg viewBox={`0 0 ${width} ${length}`} preserveAspectRatio="xMidYMid meet" className="w-full max-h-32" style={{ maxHeight: "8rem" }} aria-hidden>
      <rect x={0} y={0} width={width} height={length} rx="4" fill="#F5EFDF" stroke="#7E8C5B" strokeWidth="1" />
      {lines}
    </svg>
  );
}
